import React, { useEffect } from 'react';

import { requestFontFamily } from '../../content-studio/video-editor/fonts';
import { Graphic } from '../graphics';

const SERIF = 'Cormorant Garamond';
const SANS = 'Inter';

/**
 * A shot's words and logo drawn over its card on the board — where and how
 * the film will set them (its exact typeface, size, case, spacing, outline).
 * A preview only: positions and sizes are fractions of the frame, read with
 * container units so it scales with the card. Boxes, lines and dots are left
 * to the photo and the film.
 */
export const GraphicsPreview: React.FC<{ graphics: Graphic[]; logoUrl?: string; brandName?: string }> = ({ graphics, logoUrl, brandName }) => {
    const families = Array.from(new Set(graphics.map((g) => (g.type === 'text' ? g.family : undefined)).filter(Boolean) as string[]));
    useEffect(() => { families.forEach((f) => { void requestFontFamily(f); }); }, [families.join('|')]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="pointer-events-none absolute inset-0 overflow-hidden [container-type:size]">
            {graphics.map((g, i) => {
                if (g.type === 'text') {
                    const align = g.align || 'left';
                    const translate = align === 'center' ? '-50%' : align === 'right' ? '-100%' : '0';
                    const color = g.color || '#FFFFFF';
                    return (
                        <span
                            key={g.id || i}
                            className="absolute whitespace-pre leading-[1.1]"
                            style={{
                                left: `${g.x * 100}%`,
                                top: `${g.y * 100}%`,
                                transform: `translate(${translate}, -50%)`,
                                textAlign: align,
                                fontFamily: `"${g.family || (g.font === 'serif' ? SERIF : SANS)}", ${g.font === 'serif' ? 'serif' : 'sans-serif'}`,
                                fontSize: `${(g.size ?? 0.02) * 100}cqh`,
                                fontWeight: g.family === 'Archivo Black' ? 800 : g.weight ?? 400,
                                fontStyle: g.italic ? 'italic' : 'normal',
                                textTransform: g.upper ? 'uppercase' : 'none',
                                letterSpacing: `${(g.spacing ?? 0) / 1000}em`,
                                color: g.outline ? 'transparent' : color,
                                WebkitTextStroke: g.outline ? `max(1px, 0.025em) ${color}` : undefined,
                                textShadow: g.outline ? undefined : '0 1px 6px rgba(0,0,0,0.25)',
                            }}
                        >
                            {g.text}
                        </span>
                    );
                }
                if (g.type === 'logo') {
                    const style: React.CSSProperties = { left: `${g.x * 100}%`, top: `${g.y * 100}%`, width: `${(g.w ?? 0.3) * 100}%`, transform: 'translate(-50%, -50%)' };
                    return logoUrl
                        ? <img key={g.id || i} src={logoUrl} alt="" className="absolute object-contain" style={style} />
                        : (
                            <span key={g.id || i} className="absolute text-center font-medium uppercase tracking-[0.2em] text-white/90" style={{ ...style, fontSize: '4cqh' }}>
                                {brandName || 'Logo'}
                            </span>
                        );
                }
                return null;
            })}
        </div>
    );
};
